import React, { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import { IconButton } from '@atlaskit/button/new';
import GrowHorizontalIcon from '@atlaskit/icon/core/grow-horizontal';
import { AnalyticsContext, useAnalyticsEvents } from '@atlaskit/analytics-next';

import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient';
import {
	ContentAppearancePropertyKey,
	ContentAppearanceType,
} from '@confluence/content-appearance/entry-points/contentAppearanceTypes';
import { updateContentAppearancePropertyWithRetry } from '@confluence/content-appearance/entry-points/contentAppearance';
import { ContentAppearanceButton } from '@confluence/content-appearance/entry-points/ContentAppearanceButton';
import { fg } from '@confluence/feature-gating';

import type {
	EditContentAppearanceButtonProps,
	UpdateContentAppearancePropertyBody,
} from './editorAppearanceTypes';

const messages = defineMessages({
	adjustWidthButtonLabel: {
		id: 'editor-appearance-button.max-width.adjust-width-label',
		defaultMessage: 'Adjust width',
		description: 'Label for the adjust width button that opens the appearance dropdown',
	},
	adjustWidthButtonTooltip: {
		id: 'editor-appearance-button.max-width.adjust-width-tooltip',
		defaultMessage: 'Adjust page width',
		description: 'Tooltip for the adjust width button',
	},
	fullPageLabel: {
		id: 'editor-appearance-button.max-width.full-page-label',
		defaultMessage: 'Full page',
		description: 'Label for full page appearance option',
	},
	fullWidthLabel: {
		id: 'editor-appearance-button.max-width.full-width-label',
		defaultMessage: 'Full width',
		description: 'Label for full width appearance option',
	},
	maxLabel: {
		id: 'editor-appearance-button.max-width.max-label',
		defaultMessage: 'Max',
		description: 'Label for max width appearance option',
	},
	dropdownAriaLabel: {
		id: 'editor-appearance-button.max-width.dropdown-aria-label',
		defaultMessage: 'Page width options',
		description: 'Aria label for the page width dropdown menu',
	},
});

export type EditorAppearanceDropdownComponentProps = {
	contentId: string;
	isDisabled: boolean;
	isStatusDisconnected?: boolean;
};

const analyticsContextData = {
	packageName: '@confluence/editor-appearance-button',
	packageVersion: '0.0.1',
};

export const EditorAppearanceDropdownComponent = ({
	contentAppearanceDraft,
	contentId,
	updateContentAppearanceProperty,
	collabEditProvider,
	isDisabled,
	isStatusDisconnected,
	setContentAppearance,
}: EditContentAppearanceButtonProps) => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const apolloClient = getApolloClient();
	const [shouldDisableButton, setShouldDisableButton] = useState<boolean>(false);
	const [currentAppearance, setCurrentAppearance] = useState<ContentAppearanceType>(
		contentAppearanceDraft.appearance,
	);

	const triggerAnalyticsEvent = useCallback(
		(eventName: string, extraData = {}) => {
			const analyticDetails = {
				contentId,
				...extraData,
			};

			createAnalyticsEvent!({
				type: 'sendTrackEvent',
				data: {
					action: eventName,
					actionSubject: 'updateContentAppearanceDraft',
					source: 'EditContentAppearanceButtonComponent',
					attributes: analyticDetails,
				},
			}).fire();
		},
		[contentId, createAnalyticsEvent],
	);

	const getToggleWidthParams = useCallback((): UpdateContentAppearancePropertyBody => {
		const newVersion = contentAppearanceDraft.version ? contentAppearanceDraft.version + 1 : 1;

		return {
			contentId: contentId || '',
			propertyKey: ContentAppearancePropertyKey.DRAFT,
			propertyValue: currentAppearance,
			versionNumber: newVersion,
		};
	}, [contentAppearanceDraft.version, contentId, currentAppearance]);

	const toggleWidth = useCallback(
		async (newAppearance: ContentAppearanceType) => {
			if (contentId) {
				// Disable button to prevent clicks on it while mutation is occurring to prevent 409s
				if (fg('confluence_frontend_content_appearance_409_fixes')) {
					setShouldDisableButton(true);
				}
				setCurrentAppearance(newAppearance);

				let params = getToggleWidthParams();
				params = await updateContentAppearancePropertyWithRetry(
					updateContentAppearanceProperty,
					{ ...params, propertyValue: newAppearance },
					apolloClient,
					collabEditProvider,
				);

				if (fg('confluence_frontend_content_appearance_409_fixes')) {
					setShouldDisableButton(false);
				}

				triggerAnalyticsEvent('content.appearance.draft', {
					appearance: params.propertyValue,
					previousAppearance: contentAppearanceDraft.appearance,
					appearanceVersion: params.versionNumber,
				});
			}
		},
		[
			contentId,
			getToggleWidthParams,
			updateContentAppearanceProperty,
			apolloClient,
			collabEditProvider,
			triggerAnalyticsEvent,
			contentAppearanceDraft.appearance,
		],
	);

	return (
		<AnalyticsContext data={analyticsContextData}>
			<ContentAppearanceButton
				userOffline={isStatusDisconnected}
				apolloClient={apolloClient}
				updateContentAppearanceProperty={updateContentAppearanceProperty}
				params={getToggleWidthParams()}
				collabEditProvider={collabEditProvider}
				currentContentAppearance={contentAppearanceDraft}
				setContentAppearance={setContentAppearance}
			>
				<DropdownMenu
					trigger={({ triggerRef, ...triggerProps }) => (
						<IconButton
							{...triggerProps}
							ref={triggerRef}
							icon={GrowHorizontalIcon}
							appearance="subtle"
							spacing="default"
							isDisabled={
								shouldDisableButton ||
								// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
								(!Boolean(process.env.REACT_SSR || window.__SSR_RENDERED__) && isDisabled)
							}
							label={intl.formatMessage(messages.adjustWidthButtonTooltip)}
						/>
					)}
					placement="bottom-start"
				>
					<DropdownItemGroup>
						<DropdownItem
							onClick={() => toggleWidth(ContentAppearanceType.DEFAULT)}
							isSelected={currentAppearance === ContentAppearanceType.DEFAULT}
						>
							{intl.formatMessage(messages.fullPageLabel)}
						</DropdownItem>
						<DropdownItem
							onClick={() => toggleWidth(ContentAppearanceType.FULL_WIDTH)}
							isSelected={currentAppearance === ContentAppearanceType.FULL_WIDTH}
						>
							{intl.formatMessage(messages.fullWidthLabel)}
						</DropdownItem>
						<DropdownItem
							onClick={() => toggleWidth(ContentAppearanceType.MAX)}
							isSelected={currentAppearance === ContentAppearanceType.MAX}
						>
							{intl.formatMessage(messages.maxLabel)}
						</DropdownItem>
					</DropdownItemGroup>
				</DropdownMenu>
			</ContentAppearanceButton>
		</AnalyticsContext>
	);
};
